import { PencilSimpleIcon } from "@phosphor-icons/react/dist/csr/PencilSimple";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { GoalChecks } from "@/profitability/goal-checks";
import { profitabilityRunPath } from "@/profitability/routes";
import { ChangesSection } from "@/profitability/template-chat/workspace/changes-section";
import { PublishDraft } from "@/profitability/template-chat/workspace/publish-draft";
import type { ProfitabilityReport } from "@/profitability/types";
import { PageNotice } from "./page-notice";

/**
 * Under a published agent's header while Volca has a draft of changes for
 * it: they apply only once someone who manages the agent publishes them.
 */
export const DraftChangesBar = ({ onReview }: { onReview: () => void }) => (
  <PageNotice
    color="information"
    icon={<PencilSimpleIcon />}
    title="Volca drafted changes to this agent"
    actions={
      <Button size="sm" onClick={onReview}>
        Review changes
      </Button>
    }
  >
    They apply to the periods you run once you publish them.
  </PageNotice>
);

/**
 * The draft's changes, to publish or keep working on: what Volca says it
 * changed, and once its sample week has run, what changes in the report and
 * its figures (`ChangesSection`) and how they check against the goal. The
 * sample week opens in full on its own page.
 */
export const ReviewChangesDialog = ({
  businessId,
  report,
  open,
  onOpenChange,
  answering,
}: {
  businessId: string;
  report: ProfitabilityReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Volca is replying in Edit agent: nothing publishes meanwhile. */
  answering: boolean;
}) => {
  const navigate = useNavigate();
  const draftRun = report.draft_sample_run_id;
  const publishedRun = report.published_sample_run_id ?? null;
  const notes = report.draft_change_notes ?? [];
  const checks = report.checks?.draft;
  const comparable =
    !!draftRun &&
    !!publishedRun &&
    report.draft_sample_run_status === "completed";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review changes</DialogTitle>
          <DialogDescription>
            What Volca changed in this agent. Periods already run keep their
            results.
          </DialogDescription>
        </DialogHeader>
        <Stack
          gap={6}
          align="stretch"
          className="max-h-[60vh] min-w-0 overflow-y-auto overscroll-contain"
        >
          {comparable ? (
            <ChangesSection
              businessId={businessId}
              publishedRunId={publishedRun}
              draftRunId={draftRun}
              changeNotes={notes}
            />
          ) : (
            notes.length > 0 && (
              <ul className="list-disc space-y-1 ps-5 text-sm">
                {notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )
          )}
          {checks && (
            <Stack gap={2} align="stretch">
              <Text weight="medium">Against your figures</Text>
              <GoalChecks checks={checks} />
            </Stack>
          )}
        </Stack>
        <DialogFooter className="items-center sm:justify-between">
          {draftRun ? (
            <Button
              variant="ghost"
              onClick={() => navigate(profitabilityRunPath(draftRun))}
            >
              See the sample week
            </Button>
          ) : (
            <span />
          )}
          <PublishDraft
            businessId={businessId}
            report={report}
            answering={answering}
            onPublished={() => onOpenChange(false)}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * A published agent's draft of changes: the bar that says it is waiting
 * (not while a build is still making it) and the dialog it opens, which
 * Edit agent can open too, from the build's message.
 */
export const DraftChanges = ({
  businessId,
  report,
  answering,
  open,
  onOpenChange,
}: {
  businessId: string;
  report: ProfitabilityReport;
  answering: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <>
    {report.published &&
      report.draft_pending &&
      report.build_status !== "building" && (
        <DraftChangesBar onReview={() => onOpenChange(true)} />
      )}
    <ReviewChangesDialog
      businessId={businessId}
      report={report}
      open={open}
      onOpenChange={onOpenChange}
      answering={answering}
    />
  </>
);
